// src/components/ChatWindow.tsx
import React, { useMemo } from 'react';
import CloudDownload from '@material-ui/icons/CloudDownload';
import { FileCopy } from '@material-ui/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatWindowProps } from '../../types/deep-research';
import { IMAGE_MIME_TYPES } from '../../utils/fileAccept';


const ChatWindow: React.FC<ChatWindowProps> = ({
  chatData,
  isLoading,
  onStartResearch,
  bubbleTheme = 'default',
  progressLine = null,
}) => {
  const isLightTheme = bubbleTheme === 'light';

  const userMessageStyle = useMemo(() => {
    if (!isLightTheme) {
      return undefined;
    }
    return {
      backgroundColor: '#ffffff',
      color: '#1a1a1a',
      border: '1px solid #e0e0e0',
      borderRadius: '16px 4px 16px 16px',
    };
  }, [isLightTheme]);

  const assistantMessageStyle = useMemo(() => {
    if (!isLightTheme) {
      return undefined;
    }
    return {
      backgroundColor: '#e8f4ff',
      color: '#0d3c55',
      border: '1px solid #c9e4ff',
      borderRadius: '16px 16px 16px 4px',
    };
  }, [isLightTheme]);

  const loadingMessageStyle = useMemo(() => {
    if (!isLightTheme) {
      return undefined;
    }
    return {
      backgroundColor: '#ffffff',
      border: '1px solid #e0e0e0',
      borderRadius: '16px',
    };
  }, [isLightTheme]);

  const loadingTextStyle = useMemo(() => {
    if (!isLightTheme) {
      return undefined;
    }
    return { color: '#0d3c55' };
  }, [isLightTheme]);

  // Ensure citation / external links open in a new tab so the user doesn't lose
  // their deep-research chat session by navigating away from the page.
  const openLinksInNewTab = (htmlString: string) => {
    return htmlString.replace(/<a\b([^>]*)>/gi, (match, attrs) => {
      if (/\btarget\s*=/i.test(attrs)) {
        return match;
      }
      if (/href\s*=\s*['"]\s*#/i.test(attrs)) {
        return match;
      }
      const hasRel = /\brel\s*=/i.test(attrs);
      const relAttr = hasRel ? '' : ' rel="noopener noreferrer"';
      return `<a${attrs} target="_blank"${relAttr}>`;
    });
  };

  const parseHtmlWithStyle = (htmlString: string) => {
    // Wrap each <p> in a styled <div>
    const withSectionWrappers = htmlString.replace(
      /<p>(Section:[^\n\r<]*)([\s\S]*?)<\/p>/g,
      (_, sectionLine, rest) => {
        return `
          <div style="display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 16px;">
            <div style="border-left: 3px solid #ccc; padding-left: 12px; width: 100%;">
              <p style="margin-bottom: 0;"><strong>${sectionLine.trim()}</strong><em>${rest.trim()}</em></p>
            </div>
          </div>`;
      }
    );
    return openLinksInNewTab(withSectionWrappers);
  };

  // `/research/stream` sends raw markdown; `/research/get_pred` and every chat
  // saved before that migration send HTML, so each message carries its format.
  const renderMarkdown = useMemo(() => {
    return (content: string) => (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        linkTarget="_blank"
        components={{
          a: ({ node, children, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          p: ({ node, children, ...props }) => {
            const plainText = React.Children.toArray(children)
              .map((child) => (typeof child === 'string' ? child : ''))
              .join('')
              .trim();
            // Mirrors the old HTML path's callout for plan/section lines.
            const isSectionLine = /^Section:/i.test(plainText);
            return (
              <p {...props} className={isSectionLine ? 'deep-md-section-line' : undefined}>
                {children}
              </p>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    );
  }, []);

  const handleFileDownload = (base64?: string | null) => {
    if (!base64) return;
    let mimeType = 'application/octet-stream';
    let extension = 'bin';
    if (base64.startsWith('JVBER')) {
      mimeType = 'application/pdf';
      extension = 'pdf';
    } else if (base64.startsWith('UEsD')) {
      mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      extension = 'docx';
    }
    const downloadLink = document.createElement('a');
    downloadLink.href = `data:${mimeType};base64,${base64}`;
    downloadLink.download = `research-report.${extension}`;
    downloadLink.click();
  };

  return (
    <div className={`chat-list ${chatData ? 'full-screen' : ''}`}>
      {chatData.map((item, index) => (
        <div
          key={index}
          className={`chat-message ${item.role === 'user' ? 'user' : 'assistant'}`}
        >
          <div
            className="message-content"
            style={item.role === 'user' ? userMessageStyle : assistantMessageStyle}
          >
            {item.role === 'user' && item.attachFiles && item.attachFiles.length > 0 && (
              <div className="d-flex mb-2 overflow-auto">
                {item.attachFiles.map((file: any, fileIndex: number) => {
                  return IMAGE_MIME_TYPES.includes(file.type) ? (
                    <div className="preview-img mr-1" key={fileIndex}>
                      <img src={file.preview} alt={file.name} width="200" />
                    </div>
                  ) : (
                    <div className="preview-file" key={fileIndex}>
                      <FileCopy color="primary" />
                      <div>
                        <p>{file.name}</p>
                        <p>{file.type}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {item.format === 'markdown' ? (
              <div className="markdown-content">{renderMarkdown(item.content)}</div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: parseHtmlWithStyle(item.content) }}></div>
            )}

            <div className="button-container">
              {item.pdf && (
                <div className="pdf-download" onClick={() => handleFileDownload(item.pdf)}>
                  <i className="fas fa-file-pdf"></i>
                  <span>Download FILE  <CloudDownload color="primary" /></span>
                </div>
              )}

              {/* get_pred reports `idle` for a ready plan, the stream reports
                  `plan_ready` — both mean "waiting on the user to approve". */}
              {(item.status === 'idle' || item.status === 'plan_ready') && (
                <button
                  className={`btn research-Btn${isLoading ? ' research-Btn--disabled' : ''}`}
                  onClick={onStartResearch}
                  disabled={isLoading}
                >
                  Please go ahead
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
      {isLoading && (
        <div className="chat-message assistant">
          <div className="message-content loading-message" style={loadingMessageStyle}>
            <div className="spinner"></div>
            <p style={loadingTextStyle}>{progressLine || 'Generating response...'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
